import { create } from "zustand";
import axios, { type AxiosError } from "axios";
import { api, primeCsrf } from "@/lib/api";

export type Role = "admin" | "teacher" | "student" | "guest";

export interface AuthUser {
  userId: string;
  email?: string | null;
  displayName: string;
  role: Role;
  locale: "uk" | "de";
  mustChangePassword: boolean;
}

/** `server` — 5xx / мережа; не скидати залогіненого юзера при fetchMe. */
export type AuthError = "server" | null;

function isUnauthorizedResponse(err: unknown): boolean {
  const ax = err as AxiosError;
  if (!axios.isAxiosError(ax)) return false;
  const s = ax.response?.status;
  return s === 401 || s === 403;
}

function isServerOrNetworkError(err: unknown): boolean {
  const ax = err as AxiosError;
  if (!axios.isAxiosError(ax)) return true;
  const s = ax.response?.status;
  if (s == null) return true;
  return s >= 500;
}

interface AuthState {
  user: AuthUser | null;
  status: "idle" | "loading" | "ready";
  error: AuthError;
  fetchMe: () => Promise<void>;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  globalLogout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  setUser: (user: AuthUser | null) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  status: "idle",
  error: null,

  clearError: () => set({ error: null }),

  fetchMe: async () => {
    set({ status: "loading", error: null });
    try {
      const { data } = await api.get<AuthUser>("/auth/me");
      set({ user: data, status: "ready", error: null });
    } catch (e) {
      if (isUnauthorizedResponse(e)) {
        set({ user: null, status: "ready", error: null });
        return;
      }
      if (isServerOrNetworkError(e)) {
        set((s) => ({
          user: s.user,
          status: "ready",
          error: "server",
        }));
        return;
      }
      set({ user: null, status: "ready", error: null });
    }
  },

  login: async (email, password) => {
    await primeCsrf();
    try {
      const { data } = await api.post<AuthUser>("/auth/login", { email, password });
      set({ user: data, status: "ready", error: null });
      return data;
    } catch (e) {
      if (isServerOrNetworkError(e)) {
        set({ error: "server", status: "ready" });
      }
      throw e;
    }
  },

  logout: async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      set({ user: null });
    }
  },

  globalLogout: async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      // Leave the page immediately; clearing local state first can trigger a
      // known-session probe before Hub has completed global logout.
      window.location.assign("/api/auth/logout/redirect");
    }
  },

  changePassword: async (currentPassword, newPassword) => {
    await primeCsrf();
    const { data } = await api.post<AuthUser>("/auth/change-password", {
      currentPassword,
      newPassword,
    });
    set({ user: data, status: "ready", error: null });
  },

  setUser: (user) => set({ user }),
}));
